<script setup>
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import OrdersSortMenu from './OrdersSortMenu.vue';

defineProps({
  headerTitle: { type: String, default: 'Orders' },
  searchValue: { type: String, default: '' },
  hasAppliedFilters: { type: Boolean, default: false },
  activeSort: { type: String, default: 'placed_at' },
  activeOrdering: { type: String, default: '-' },
});

const emit = defineEmits([
  'search',
  'newOrder',
  'import',
  'export',
  'toggleAdvanceFilters',
  'update:sort',
  'saveFilter',
  'openColumnConfig',
]);

const handleNewOrder = () => {
  emit('newOrder');
};

const handleImport = () => {
  emit('import');
};

const handleExport = () => {
  emit('export');
};

const handleToggleFilters = () => {
  emit('toggleAdvanceFilters');
};

const handleSaveFilter = () => {
  emit('saveFilter');
};

const handleOpenColumnConfig = () => {
  emit('openColumnConfig');
};
</script>

<template>
  <header class="sticky top-0 z-10">
    <!-- Mobile Layout (< md) -->
    <div class="xl:hidden">
      <!-- First row: Title and essential actions -->
      <div class="flex items-center justify-between w-full h-20 px-4 gap-2 pb-1.5">
        <span class="text-base ml-10 xl:ml-0 font-semibold truncate text-n-slate-12 flex-1 min-w-0">
          {{ headerTitle }}
        </span>
        <div class="flex items-center gap-2 flex-shrink-0">
          <Button
            class="!bg-grass-400 font-medium !h-8 px-3 text-sm"
            icon="i-lucide-plus"
            trailing-icon
            :label="$t('ORDERS_LAYOUT.HEADER.NEW_ORDER')"
            size="sm"
            @click="handleNewOrder"
          />
        </div>
      </div>
      
      <!-- Second row: Search and more actions -->
      <div class="flex items-center gap-2 px-4 pb-3">
        <div class="flex-1 min-w-0">
          <Input
            :model-value="searchValue"
            type="search"
            :placeholder="$t('ORDERS_LAYOUT.HEADER.SEARCH_PLACEHOLDER')"
            :custom-input-class="[
              'h-9 ltr:!pl-3 ltr:!pr-8 rtl:!pr-3 rtl:!pl-8 !py-1 text-sm',
            ]"
            @input="emit('search', $event.target.value)"
          >
            <template #suffix>
              <Icon
                icon="i-lucide-search"
                class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:right-2 rtl:left-2"
              />
            </template>
          </Input>
        </div>
        <div class="flex items-center gap-1 flex-shrink-0">
          <Button
            id="toggleOrdersFilterButton"
            icon="i-lucide-list-filter"
            color="slate"
            size="sm"
            class="relative w-8 h-8"
            variant="ghost"
            @click="handleToggleFilters"
          >
            <div
              v-if="hasAppliedFilters"
              class="absolute top-0 right-0 w-2 h-2 rounded-full bg-n-brand"
            />
          </Button>
          <Button
            v-if="hasAppliedFilters"
            icon="i-lucide-save"
            color="slate"
            size="sm"
            class="w-8 h-8"
            variant="ghost"
            @click="handleSaveFilter"
          />
          <OrdersSortMenu
            :active-sort="activeSort"
            :active-ordering="activeOrdering"
            @update:sort="emit('update:sort', $event)"
          />
          <!-- More actions dropdown for mobile -->
          <div class="relative">
            <Button
              icon="i-lucide-ellipsis-vertical"
              color="slate"
              size="sm"
              class="w-8 h-8"
              variant="ghost"
            />
            <!-- Dropdown menu can be added here for import/export/settings -->
          </div>
        </div>
      </div>
      <slot name="filter" />
    </div>

    <!-- Desktop/Tablet Layout (≥ md) -->
    <div class="hidden xl:flex">
      <div class="flex items-center justify-between w-full h-10 gap-4 mt-4 px-3 lg:px-6">
        <span class="ml-10 xl:ml-0 pb-1.5 xl:pb-0 text-base xl:text-lg font-semibold truncate text-n-slate-12">
          {{ headerTitle }}
        </span>
        <div class="flex items-center flex-shrink-0 gap-4">
          <div class="flex items-center gap-2">
            <Input
              :model-value="searchValue"
              type="search"
              :placeholder="$t('ORDERS_LAYOUT.HEADER.SEARCH_PLACEHOLDER')"
              :custom-input-class="[
                'h-10 ltr:!pl-8 ltr:!pr-8 rtl:!pr-8 rtl:!pl-8 xl:ltr:!pr-2 xl:rtl:!pl-2 !py-1',
              ]"
              @input="emit('search', $event.target.value)"
            >
              <!-- Desktop: Icon as prefix (left side) -->
              <template #prefix>
                <Icon
                  icon="i-lucide-search"
                  class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2 rtl:right-2 hidden xl:block"
                />
              </template>
              
              <!-- Tablet: Icon as suffix (right side) -->
              <template #suffix>
                <Icon
                  icon="i-lucide-search"
                  class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:right-2 rtl:left-2 xl:hidden"
                />
              </template>
            </Input>
          </div>
          <div class="flex items-center gap-2">
            <div class="relative">
              <Button
                id="toggleOrdersFilterButton"
                icon="i-lucide-list-filter"
                color="slate"
                size="sm"
                class="relative w-8"
                variant="ghost"
                @click="handleToggleFilters"
              >
                <div
                  v-if="hasAppliedFilters"
                  class="absolute top-0 right-0 w-2 h-2 rounded-full bg-n-brand"
                />
              </Button>
              <slot name="filter" />
            </div>
            <div class="relative">
              <Button
                v-if="hasAppliedFilters"
                v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.ADD.SAVE_BUTTON')"
                icon="i-lucide-save"
                color="slate"
                size="sm"
                variant="ghost"
                @click="handleSaveFilter"
              />
              <div
                id="saveFilterTeleportTarget"
                class="absolute z-40 mt-2 ltr:right-0 rtl:left-0"
              />
            </div>
            <OrdersSortMenu
              :active-sort="activeSort"
              :active-ordering="activeOrdering"
              @update:sort="emit('update:sort', $event)"
            />
            <Button
              icon="i-lucide-download"
              color="slate"
              size="sm"
              variant="ghost"
              @click="handleImport"
            />
            <Button
              icon="i-lucide-upload"
              color="slate"
              size="sm"
              variant="ghost"
              @click="handleExport"
            />
            <Button
              icon="i-lucide-settings"
              color="slate"
              size="sm"
              variant="ghost"
              @click="handleOpenColumnConfig"
              :title="$t('COLUMN_CONFIG.BUTTON_TITLE')"
            />
          </div>
          <div class="w-px h-4 bg-n-strong" />
          <Button
            class="!bg-grass-400 font-medium !h-10 p-[10px]"
            icon="i-lucide-plus"
            trailing-icon
            :label="$t('ORDERS_LAYOUT.HEADER.NEW_ORDER')"
            size="sm"
            @click="handleNewOrder"
          />
        </div>
      </div>
    </div>
  </header>
</template>
